import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { adminService } from '../../services/api';

const Users = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    role: 'customer',
    password: ''
  });

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await adminService.getUsers();
      setUsers(response.data);
      setError(null);
    } catch (err) {
      setError('Kullanıcılar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpenDialog = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone,
        role: user.role,
        password: ''
      });
    } else {
      setSelectedUser(null);
      setFormData({
        name: '',
        email: '',
        phone: '',
        role: 'customer',
        password: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setFormData({
      name: '',
      email: '',
      phone: '',
      role: 'customer',
      password: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedUser) {
        await adminService.updateUser(selectedUser.id, formData);
      } else {
        await adminService.createUser(formData);
      }
      handleCloseDialog();
      fetchUsers();
    } catch (err) {
      setError('İşlem sırasında bir hata oluştu.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu kullanıcıyı silmek istediğinizden emin misiniz?')) {
      try {
        await adminService.deleteUser(id);
        fetchUsers();
      } catch (err) {
        setError('Kullanıcı silinirken bir hata oluştu.');
      }
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'mechanic':
        return 'warning';
      default:
        return 'primary';
    }
  };

  // Filtrelenmiş ve aranmış kullanıcılar
  const filteredUsers = useMemo(() => {
    return users.filter(user => {
      // Arama
      const search = searchTerm.toLowerCase();
      const matchesSearch =
        user.name.toLowerCase().includes(search) ||
        (user.email && user.email.toLowerCase().includes(search)) ||
        (user.phone && user.phone.toLowerCase().includes(search)) ||
        (user.role && user.role.toLowerCase().includes(search));
      // Rol
      const matchesRole = roleFilter === 'all' || user.role === roleFilter;
      // Durum (aktif/pasif)
      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus = user.isActive !== false;
      } else if (statusFilter === 'inactive') {
        matchesStatus = user.isActive === false;
      }
      return matchesSearch && matchesRole && matchesStatus;
    });
  }, [users, searchTerm, roleFilter, statusFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Başlık ve aksiyonlar */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} justifyContent="space-between" alignItems={isMobile ? 'stretch' : 'center'} mb={3} gap={isMobile ? 2 : 0}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" fontWeight={600}>
          Kullanıcı Yönetimi
        </Typography>
        <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={isMobile ? 1 : 0}>
          <Button
            variant="contained"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0, width: isMobile ? '100%' : 'auto' }}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yenile
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            size={isMobile ? 'large' : 'medium'}
            fullWidth={isMobile}
          >
            Yeni Kullanıcı
          </Button>
        </Box>
      </Box>

      {/* Arama ve filtre alanları */}
      <Box display="flex" flexDirection={isMobile ? 'column' : 'row'} gap={2} mb={3} alignItems={isMobile ? 'stretch' : 'center'}>
        <TextField
          placeholder="İsim, e-posta, telefon veya rol ara..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          size={isMobile ? 'large' : 'small'}
          fullWidth={isMobile}
        />
        <FormControl fullWidth={isMobile} size={isMobile ? 'large' : 'small'}>
          <InputLabel>Rol</InputLabel>
          <Select
            value={roleFilter}
            onChange={e => setRoleFilter(e.target.value)}
            label="Rol"
          >
            <MenuItem value="all">Tümü</MenuItem>
            <MenuItem value="customer">Müşteri</MenuItem>
            <MenuItem value="mechanic">Usta</MenuItem>
            <MenuItem value="admin">Yönetici</MenuItem>
          </Select>
        </FormControl>
        <FormControl fullWidth={isMobile} size={isMobile ? 'large' : 'small'}>
          <InputLabel>Durum</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            label="Durum"
          >
            <MenuItem value="all">Tümü</MenuItem>
            <MenuItem value="active">Aktif</MenuItem>
            <MenuItem value="inactive">Pasif</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, fontWeight: 500, fontSize: isMobile ? 14 : 16 }}>
          {error}
        </Alert>
      )}

      {/* Masaüstü Tablo */}
      {!isMobile && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Ad Soyad</TableCell>
                <TableCell>E-posta</TableCell>
                <TableCell>Telefon</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, fontWeight: 500, color: 'text.secondary' }}>
                    Kriterlere uygun kullanıcı bulunamadı.
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user.id} sx={{ '&:hover': { background: 'action.hover' } }}>
                    <TableCell>
                      <Typography variant="subtitle1" fontWeight={600}>{user.name}</Typography>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{user.phone}</TableCell>
                    <TableCell>
                      <Chip
                        label={user.role === 'admin' ? 'Yönetici' : user.role === 'mechanic' ? 'Usta' : 'Müşteri'}
                        color={getRoleColor(user.role)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Düzenle">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog(user)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sil">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(user.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Mobil Kart/Kutu Görünümü */}
      {isMobile && (
        <Box>
          {filteredUsers.length === 0 ? (
            <Alert severity="info" sx={{ my: 4, fontWeight: 500, fontSize: 15 }}>
              Kriterlere uygun kullanıcı bulunamadı.
            </Alert>
          ) : (
            filteredUsers.map((user) => (
              <Card key={user.id} sx={{ mb: 2, p: 2, borderRadius: 2, boxShadow: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} mb={1}>{user.name}</Typography>
                <Typography variant="body2" color="text.secondary" mb={0.5}>{user.email}</Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>{user.phone}</Typography>
                <Box display="flex" gap={1} mb={1}>
                  <Chip
                    label={user.role === 'admin' ? 'Yönetici' : user.role === 'mechanic' ? 'Usta' : 'Müşteri'}
                    color={getRoleColor(user.role)}
                    size="small"
                  />
                </Box>
                <Box display="flex" gap={1} mt={1}>
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => handleOpenDialog(user)}
                    size="small"
                    fullWidth
                  >
                    Düzenle
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleDelete(user.id)}
                    size="small"
                    fullWidth
                  >
                    Sil
                  </Button>
                </Box>
              </Card>
            ))
          )}
        </Box>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedUser ? 'Kullanıcıyı Düzenle' : 'Yeni Kullanıcı Ekle'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Ad Soyad"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="E-posta"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Telefon"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Rol</InputLabel>
                  <Select
                    value={formData.role}
                    label="Rol"
                    onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                    required
                  >
                    <MenuItem value="customer">Müşteri</MenuItem>
                    <MenuItem value="mechanic">Usta</MenuItem>
                    <MenuItem value="admin">Yönetici</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Şifre"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!selectedUser}
                  helperText={selectedUser ? 'Şifreyi değiştirmek istemiyorsanız boş bırakın' : ''}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>İptal</Button>
            <Button type="submit" variant="contained" color="primary">
              {selectedUser ? 'Güncelle' : 'Ekle'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default Users; 